(() => {
  const instances = new Map();
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const init = (scope = document) => {
    for (const root of scope.querySelectorAll('[data-sr-builder]')) {
      if (instances.has(root)) continue;
      const abort = new AbortController();
      instances.set(root, abort);
      const labels = JSON.parse(root.querySelector('[data-sr-labels]').textContent);
      const state = { active: 'pot', pot: null, tea: null, cup: null, busy: false, added: false };
      const groups = [...root.querySelectorAll('[data-sr-stage]')].map(panel => panel.dataset.srStage);
      const complete = () => groups.reduce((count, group) => count + (state[group]?.quantity || 0), 0) >= 3;
      const cards = [...root.querySelectorAll('[data-sr-product]')];
      const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: root.dataset.currency || 'USD'
      }).format(cents / 100);
      const status = root.querySelector('[data-sr-status]');
      const add = root.querySelector('[data-sr-add]');
      const lines = root.querySelector('[data-sr-lines]');
      const defaultCartText = root.querySelector('.sr-summary__cart').textContent;
      const total = root.querySelector('[data-sr-total]');
      const summary = root.querySelector('.sr-summary');
      const disclosure = root.querySelector('[data-sr-disclosure]');
      const compact = matchMedia('(max-width: 749px)');
      const layout = root.querySelector('.sr-builder__layout');
      const positionSummary = () => {
        layout.append(summary);
        disclosure.open = !compact.matches;
      };
      positionSummary();
      compact.addEventListener('change', positionSummary, { signal: abort.signal });
      disclosure.querySelector('summary').addEventListener('click', event => {
        if (!compact.matches) event.preventDefault();
      }, { signal: abort.signal });
      const currentOption = card => card.querySelector('[data-sr-variant]').selectedOptions[0];
      const notify = (message, error = false) => { status.textContent = message; status.dataset.error = String(error); };
      const scroll = element => element.scrollIntoView({ behavior: reduced() ? 'instant' : 'smooth', block: 'start' });
      const updateTotal = () => {
        const value = money(groups.reduce((sum, group) => sum + (state[group] ? state[group].price * state[group].quantity : 0), 0));
        total.textContent = value;
        root.querySelector('[data-sr-mini-total]').textContent = value;
      };
      const render = () => {
        root.classList.add('sr-enhanced');
        root.dataset.step = state.active;
        root.classList.toggle('sr-is-added', state.added);
        root.setAttribute('aria-busy', String(state.busy));
        root.querySelectorAll('[data-sr-enhancement]').forEach(el => { el.hidden = false; });
        root.querySelectorAll('[data-sr-stage]').forEach(el => { el.hidden = el.dataset.srStage !== state.active; });
        root.querySelectorAll('[data-sr-step]').forEach(el => {
          if (el.dataset.srStep === state.active) el.setAttribute('aria-current', 'step');
          else el.removeAttribute('aria-current');
          el.setAttribute('aria-disabled', String(!state.pot && el.dataset.srStep !== 'pot'));
          el.classList.toggle('sr-step-complete', Boolean(state[el.dataset.srStep]));
        });
        for (const card of cards) {
          const option = currentOption(card);
          const selected = state[card.dataset.group]?.id === option.value;
          const choose = card.querySelector('[data-sr-choose]');
          choose.hidden = false;
          choose.disabled = state.busy || state.added || option.dataset.available !== 'true';
          choose.textContent = selected ? labels.selected : option.dataset.available === 'true' ? labels.choose : labels.soldOut;
          choose.setAttribute('aria-pressed', String(selected));
          card.classList.toggle('sr-choice--selected', selected);
          const photo = card.querySelector('.sr-choice__image img');
          if (option.dataset.image) {
            photo.removeAttribute('srcset');
            photo.src = option.dataset.image;
            photo.alt = `${card.dataset.title} — ${option.dataset.variantTitle}`;
          }
          const imageNote = card.querySelector('[data-sr-image-note]');
          if (imageNote) imageNote.hidden = Boolean(option.dataset.image);
          card.querySelector('[data-sr-price]').textContent = money(Number(option.dataset.price));
          card.querySelector('[data-sr-native-id]').value = option.value;
          card.querySelector('[data-sr-variant]').disabled = state.busy || state.added;
        }
        lines.replaceChildren();
        for (const group of groups) {
          const choice = state[group];
          if (!choice) {
            const placeholder = document.createElement('button');
            placeholder.type = 'button'; placeholder.className = 'sr-selection__empty';
            placeholder.dataset.srStep = group;
            placeholder.disabled = state.busy || state.added || (!state.pot && group !== 'pot');
            const number = document.createElement('span'); number.textContent = `0${groups.indexOf(group) + 1}`;
            const text = document.createElement('span'); text.textContent = labels[`pick${group[0].toUpperCase()}${group.slice(1)}`];
            const note = document.createElement('span'); note.className = 'sr-selection__empty-note';
            note.textContent = group === 'pot' ? '↗' : labels.optional;
            placeholder.append(number, text, note); lines.append(placeholder); continue;
          }
          const line = document.createElement('div');
          line.className = 'sr-summary-line';
          const category = document.createElement('span');
          category.className = 'sr-summary-line__category';
          category.textContent = labels[group];
          const title = document.createElement('a');
          title.href = choice.url;
          title.textContent = group === 'pot' ? choice.title.split(' — ')[0] : choice.title;
          const variant = document.createElement('span');
          variant.className = 'sr-summary-line__variant';
          variant.textContent = choice.variant === 'Default Title' ? money(choice.price) : `${choice.variant} · ${money(choice.price)}`;
          const controls = document.createElement('div');
          controls.className = 'sr-summary-line__controls';
          const label = document.createElement('label');
          label.htmlFor = `${root.id}-quantity-${group}`;
          label.textContent = labels.quantity;
          const quantity = document.createElement('input');
          quantity.id = label.htmlFor;
          quantity.type = 'number';
          quantity.min = '1'; quantity.max = '9'; quantity.step = '1';
          quantity.value = String(choice.quantity);
          quantity.dataset.srQuantity = group;
          quantity.disabled = state.busy || state.added;
          controls.append(label, quantity);
          if (group !== 'pot') {
            const remove = document.createElement('button');
            remove.type = 'button'; remove.className = 'sr-text-button';
            remove.dataset.srRemove = group; remove.textContent = labels.remove;
            remove.setAttribute('aria-label', `${labels.remove} ${choice.title}`);
            remove.disabled = state.busy || state.added; controls.append(remove);
          }
          const edit = document.createElement('button');
          edit.type = 'button'; edit.className = 'sr-text-button sr-summary-line__edit';
          edit.dataset.srEdit = group; edit.textContent = labels.edit;
          edit.setAttribute('aria-label', `${labels.edit} ${choice.title}`);
          edit.disabled = state.busy || state.added;
          const photo = document.createElement('img');
          photo.src = choice.card.querySelector('.sr-choice__image img').currentSrc || choice.card.querySelector('.sr-choice__image img').src;
          photo.alt = ''; photo.width = 56; photo.height = 64;
          const details = document.createElement('div'); details.className = 'sr-summary-line__details';
          details.append(category, title, variant, controls);
          line.append(photo, details, edit);
          lines.append(line);
        }
        
        root.querySelectorAll('[data-sr-next]').forEach(el => { el.disabled = !state.pot || state.busy || state.added; });
        root.querySelectorAll('[data-sr-skip],[data-sr-review]').forEach(el => { el.disabled = state.busy || state.added; });
        const forward = root.querySelector('[data-sr-forward]');
        forward.hidden = state.active === 'cup' || state.added;
        forward.disabled = !state.pot || state.busy;
        const nextGroup = groups[groups.indexOf(state.active) + 1];
        forward.dataset.srNext = nextGroup || 'cup';
        forward.textContent = nextGroup === 'tea' ? labels.nextTea : labels.nextCup;
        const back = root.querySelector('[data-sr-back]');
        back.hidden = state.active === 'pot' || state.added;
        back.dataset.srNext = groups[Math.max(0, groups.indexOf(state.active) - 1)];
        back.disabled = state.busy;
        const skip = root.querySelector('[data-sr-skip]');
        skip.hidden = state.active === 'pot' || state.added || (state.active === 'tea' && !state.tea) || (state.active === 'cup' && !state.cup);
        skip.dataset.srSkip = state.active;
        skip.textContent = state.active === 'tea' ? labels.skipTea : labels.skipCup;
        skip.disabled = state.busy;
        root.querySelector('[data-sr-progress]').textContent = state.added ? '' : `${String(groups.indexOf(state.active) + 1).padStart(2, '0')} / ${String(groups.length).padStart(2, '0')} · ${labels[`stage${state.active[0].toUpperCase()}${state.active.slice(1)}`]}`;
        root.querySelector('[data-sr-restart]').hidden = !state.added;
        const cartLink = root.querySelector('.sr-summary__cart');
        cartLink.classList.toggle('sr-button', state.added);
        add.hidden = state.added || state.active !== 'cup';
        add.classList.remove('sr-button--secondary');
        add.disabled = !state.pot || state.busy || state.added;
        add.textContent = state.busy ? labels.adding : complete() ? labels.addWithBenefits : labels.add;
        const chosenCount = Math.min(3, groups.reduce((n, group) => n + (state[group]?.quantity || 0), 0));
        const rewardProgress = root.querySelector('[data-sr-reward-progress]');
        if (rewardProgress) rewardProgress.textContent = labels[`reward${chosenCount}`];
        const unlockedBenefits = root.querySelector('[data-sr-benefits-unlocked]');
        if (unlockedBenefits) unlockedBenefits.hidden = !complete();
        const offer = root.querySelector('[data-sr-offer]');
        if (offer) { offer.hidden = !complete(); if (!complete()) offer.open = false; }
        cartLink.textContent = state.added && complete() ? labels.offerCart : defaultCartText;
        const discount = root.dataset.offerDiscount;
        cartLink.href = state.added && complete() && discount ? `${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent((window.Shopify?.routes?.root || '/') + 'cart')}` : (window.Shopify?.routes?.root || '/') + 'cart';
        updateTotal();
      };
      const choose = (card, toggle = true) => {
        const option = currentOption(card);
        if (state.added) return;
        if (option.dataset.available !== 'true') return notify(labels.soldOut);
        const group = card.dataset.group;
        const previous = state[group];
        state[group] = toggle && group !== 'pot' && previous?.id === option.value ? null : {
          id: option.value, variant: option.dataset.variantTitle || option.textContent.trim(), title: card.dataset.title,
          price: Number(option.dataset.price), url: card.dataset.url,
          quantity: previous?.quantity || 1, card
        };
        render();
        notify(state[group] ? `${labels[group]}: ${state[group].title}${state[group].variant === 'Default Title' ? '' : ` — ${state[group].variant}`}` : labels.remove);
      };
      const enter = (group, focus = true) => {
        if (group !== 'pot' && !state.pot) { notify(labels.needPot); return; }
        state.active = group; if (!state.added) notify(''); render();
        if (focus) {
          if (compact.matches) disclosure.open = false;
          const panel = root.querySelector(`[data-sr-stage="${group}"]`);
          panel.querySelector('h3').focus({ preventScroll: true }); scroll(panel);
        }
      };
      const review = () => {
        disclosure.open = !compact.matches;
        summary.focus({ preventScroll: true }); scroll(summary);
      };
      const addChoices = async () => {
        if (state.busy || state.added || !state.pot) return;
        state.busy = true; notify(''); render();
        const items = groups.filter(g => state[g]).map(g => ({ id: Number(state[g].id), quantity: state[g].quantity, ...(complete() ? { properties: { 'Tea table offer': 'Ebook, VIP and priority processing after purchase; 3+ paid items', 'Priority handling': 'Priority order processing ahead of standard orders', 'VIP Tea Circle': 'Membership included after purchase; merchant fulfillment' } } : {}) }));
        const base = window.Shopify?.routes?.root || '/';
        let eventBridge, deferred;
        try {
          try {
            eventBridge = await import('@shopify/events');
            deferred = eventBridge.CartLinesUpdateEvent.createPromise();
            deferred.promise.catch(() => {});
            document.dispatchEvent(new eventBridge.CartLinesUpdateEvent({
              action: 'add', context: 'product',
              lines: items.map(i => ({ merchandiseId: String(i.id), quantity: i.quantity })),
              promise: deferred.promise
            }));
          } catch { eventBridge = null; deferred = null; }
          const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
          const response = await fetch(`${base}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items, ...(sections.length ? { sections, sections_url: location.pathname } : {}) }),
            signal: abort.signal
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || labels.error);
          let cart;
          if (window.SnowshanGiftCart) {
            try {
              const gifts = await window.SnowshanGiftCart.sync({ announce: false });
              cart = gifts.cart;
              if (gifts.sections) result.sections = gifts.sections;
            } catch { notify(labels.error, true); }
          }
          try {
            const refreshed = await fetch(`${base}cart.js`, { signal: abort.signal });
            if (refreshed.ok) cart = await refreshed.json();
          } catch {}
          if (deferred && cart) deferred.resolve({
            cart: eventBridge.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, sections: result.sections, didError: false, source: 'snowshan-ritual', itemCount: items.reduce((s,i) => s+i.quantity,0) }
          });
          else if (deferred) deferred.reject(new Error('Cart refresh unavailable'));
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, sections: result.sections } }));
          const drawer = document.querySelector('cart-drawer');
          if (!eventBridge && drawer?.renderContents && result.sections) drawer.renderContents(result);
          state.added = true;
          notify(labels.added);
        } catch (error) {
          deferred?.reject(error);
          if (error.name !== 'AbortError') notify(error.message || labels.error, true);
        } finally {
          state.busy = false; render();
          if (state.added) review();
        }
      };
      root.addEventListener('click', event => {
        const target = event.target.closest('button,a');
        if (!target || state.busy) return;
        if (target.matches('[data-sr-restart]')) {
          state.added = false; groups.forEach(group => { state[group] = null; });
          notify(''); enter('pot');
        }
        else if (target.matches('[data-sr-edit]')) {
          event.preventDefault(); enter(target.dataset.srEdit);
          const card = state[target.dataset.srEdit]?.card;
          if (card) { if (compact.matches) disclosure.open = false; const variant = card.querySelector('[data-sr-variant]'); (variant.hidden ? card.querySelector('[data-sr-choose]') : variant).focus({ preventScroll: true }); scroll(card); }
        }
        else if (state.added && !target.matches('[data-sr-step],a')) return;
        else if (target.matches('[data-sr-choose]')) {
          event.preventDefault(); const card = target.closest('[data-sr-product]'); const group = card.dataset.group;
          choose(card);
          if (state[group]) { const next = groups[groups.indexOf(group) + 1]; if (next) enter(next); else review(); }
        }
        else if (target.matches('[data-sr-step]')) { event.preventDefault(); enter(target.dataset.srStep); }
        else if (target.matches('[data-sr-next]')) { event.preventDefault(); enter(target.dataset.srNext); }
        else if (target.matches('[data-sr-skip]')) {
          event.preventDefault(); const group = target.dataset.srSkip; state[group] = null; notify('');
          if (group === 'tea') enter('cup'); else { render(); review(); }
        }
        else if (target.matches('[data-sr-remove]')) { state[target.dataset.srRemove] = null; notify(''); render(); }
        else if (target.matches('[data-sr-review]')) { event.preventDefault(); review(); }
        else if (target.matches('[data-sr-add]')) { event.preventDefault(); addChoices(); }
      }, { signal: abort.signal });
      root.addEventListener('change', event => {
        if (state.busy || state.added) return;
        const element = event.target;
        if (element.matches('[data-sr-variant]')) {
          const card = element.closest('[data-sr-product]');
          if (state[card.dataset.group]?.card === card) choose(card, false); else render();
        }
        if (element.matches('[data-sr-quantity]')) {
          const choice = state[element.dataset.srQuantity];
          if (!choice) return;
          choice.quantity = Math.max(1, Math.min(9, Math.round(Number(element.value) || 1)));
          element.value = String(choice.quantity); notify(''); render();
        }
      }, { signal: abort.signal });
      document.addEventListener('click', event => {
        const start = event.target.closest('[data-ritual-start]');
        if (!start || state.added || !document.contains(root)) return;
        const card = cards.find(card => card.dataset.group === 'pot' && card.dataset.handle === start.dataset.ritualStart);
        if (card) { event.preventDefault(); choose(card, false); enter(start.dataset.ritualStep === 'tea' ? groups[1] : 'pot'); }
      }, { signal: abort.signal });
      render();
      const requested = new URL(location.href).searchParams.get('ritual_pot');
      const requestedCard = requested && cards.find(card => card.dataset.group === 'pot' && card.dataset.handle === requested);
      if (requestedCard) { choose(requestedCard, false); enter('pot', false); }
    }
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, abort] of instances) if (event.target.contains(root)) { abort.abort(); instances.delete(root); }
  });
})();

